import {CopyIcon, LockIcon, SyncIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, RelativeTime, Text} from '@primer/react'
import {SkeletonText} from '@primer/react/experimental'
import {memo, useState} from 'react'

import type {Recap} from '../../api/scribe/types'
import styles from './recap-content.module.css'

const PERIOD_OPTIONS = [
  {value: 'updated:>@today-1w', label: 'Past week'},
  {value: 'iteration:@current', label: 'Current iteration'},
  {value: 'iteration:@previous', label: 'Previous iteration'},
] as const

type PeriodValue = (typeof PERIOD_OPTIONS)[number]['value']

export function formatRecapDateRange(recap: Recap): {dateString: string; showWeekOf: boolean} {
  if (recap.start_time && recap.end_time) {
    const startDate = new Date(recap.start_time)
    const endDate = new Date(recap.end_time)
    const startStr = startDate.toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'})
    const endStr = endDate.toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'})
    return {dateString: `${startStr} — ${endStr}`, showWeekOf: false}
  }
  if (recap.created_at) {
    return {
      dateString: new Date(recap.created_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      showWeekOf: true,
    }
  }
  return {dateString: '', showWeekOf: true}
}

interface RecapSummaryProps {
  showLoadingSpinner: boolean
  latestRecap: Recap | null | undefined
  onRegenerate: (filterQuery: string) => void
  onCopy: () => void
  isGeneratingRecap: boolean
}

export const RecapSummary = memo(function RecapSummary({
  showLoadingSpinner,
  latestRecap,
  onRegenerate,
  onCopy,
  isGeneratingRecap,
}: RecapSummaryProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodValue>(PERIOD_OPTIONS[0].value)
  const selectedOption = PERIOD_OPTIONS.find(opt => opt.value === selectedPeriod)
  const selectedLabel = selectedOption ? selectedOption.label : PERIOD_OPTIONS[0].label

  if (showLoadingSpinner) {
    return (
      <div className={styles.loadingHeader}>
        <div className={styles.skeletonText}>
          <SkeletonText lines={1} aria-label="Generating recap header" />
        </div>
      </div>
    )
  }

  if (!latestRecap?.created_at) {
    return null
  }

  const {dateString, showWeekOf} = formatRecapDateRange(latestRecap)

  return (
    <div className={styles.recapHeader}>
      <h2 className={styles.insightTitle}>
        {showWeekOf ? `Week of ${dateString}` : dateString}
        <span className={styles.recapDate}>
          Generated <RelativeTime datetime={latestRecap.created_at} /> · <LockIcon size={12} /> Only visible to you
        </span>
      </h2>
      <div className={styles.recapActions}>
        <ActionMenu>
          <ActionMenu.Button>
            <Text weight="light" style={{color: 'var(--fgColor-muted)'}}>
              Period:
            </Text>{' '}
            {selectedLabel}
          </ActionMenu.Button>
          <ActionMenu.Overlay>
            <ActionList selectionVariant="single">
              {PERIOD_OPTIONS.map(option => (
                <ActionList.Item
                  key={option.value}
                  selected={selectedPeriod === option.value}
                  onSelect={() => setSelectedPeriod(option.value)}
                >
                  {option.label}
                </ActionList.Item>
              ))}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
        <IconButton
          aria-label="Regenerate recap"
          icon={SyncIcon}
          size="small"
          onClick={() => onRegenerate(selectedPeriod)}
          disabled={isGeneratingRecap}
        />
        <IconButton
          aria-label="Copy recap markdown"
          icon={CopyIcon}
          size="small"
          onClick={onCopy}
          disabled={isGeneratingRecap}
        />
      </div>
    </div>
  )
})

try{ RecapSummary.displayName ||= 'RecapSummary' } catch {}